import { useState } from "react";
import { LanderV2StepProps } from "../../../Campaigns/Schemas/LanderV2/types";
import { IQuote } from "../../../../models/QuoteData";
import { getProviderLogo, formatCurrency } from "../../../../modules/common/quotes/quoteUtils";
import { variantBAssets } from "../../../Utils/variantBAssets";
import { TrustpilotReviewsCarousel } from "../TrustpilotReviewsCarousel";
import { QuoteExplainerDrawer, QuoteExplainerType } from "../shared/QuoteExplainerDrawer";
import { QuoteAdviceActions } from "../shared/QuoteAdviceActions";
import { QuoteBoostBand } from "../shared/QuoteBoostBand";

const landerCallNumber = "0808 281 2558";
const landerCallHref = "tel:08082812558";

function quoteKey(quote: IQuote, index: number) {
  return (
    quote.quoteID ??
    quote.quoteNumber ??
    `${quote.providerName ?? "quote"}-${quote.productName ?? "annuity"}-${quote.annuityAmountPA ?? "0"}-${index}`
  );
}

const VARIANT_PROVIDER_LOGOS: Record<string, string> = {
  aviva: variantBAssets.providerAviva,
  "canada life": variantBAssets.providerCanadaLife,
  just: variantBAssets.providerJust,
  "just retirement": variantBAssets.providerJust,
  "legal & general": variantBAssets.providerLegalGeneral,
  "legal and general": variantBAssets.providerLegalGeneral,
  lv: variantBAssets.providerLv,
  "lv=": variantBAssets.providerLv,
  prudential: variantBAssets.providerPrudential,
  "scottish widows": variantBAssets.providerScottishWidows,
  "standard life": variantBAssets.providerStandardLife,
};

function getQuoteLogo(providerName?: string): string | null {
  if (!providerName) return null;
  return VARIANT_PROVIDER_LOGOS[providerName.toLowerCase().trim()] ?? getProviderLogo(providerName);
}

function getQuoteDetail(quote: IQuote) {
  return isFixedTermQuote(quote) ? "Single Life, Fixed Term, 5 Years" : "Single Life, Enhanced Level";
}

function isFixedTermQuote(quote: IQuote) {
  const name = `${quote.productName ?? ""} ${quote.productType ?? ""}`.toLowerCase();
  const termYears = Number(quote.termYears ?? 0);

  return name.includes("fixed") || (termYears > 0 && termYears < 999);
}

type QuoteGroup = {
  title: string;
  type: QuoteExplainerType;
  hero: IQuote;
  rows: IQuote[];
};

function buildQuoteGroup(title: string, type: QuoteExplainerType, quotes: IQuote[]): QuoteGroup | null {
  const sorted = [...quotes].sort((a, b) => Number(b.annuityAmountPA ?? 0) - Number(a.annuityAmountPA ?? 0));
  const [hero, ...rows] = sorted;

  return hero ? { title, type, hero, rows } : null;
}

function getQuoteGroups(quotes: IQuote[]) {
  const lifetimeQuotes = quotes.filter((quote) => !isFixedTermQuote(quote));
  const fixedTermQuotes = quotes.filter(isFixedTermQuote);

  return [
    buildQuoteGroup("Best Lifetime Annuity", "lifetime", lifetimeQuotes),
    buildQuoteGroup("Best Fixed Term Annuity", "fixed", fixedTermQuotes),
  ].filter((group): group is QuoteGroup => Boolean(group));
}

function QuotesFallbackPanel() {
  return (
    <div>
      <div className="lv2-funnel-header">
        <h2>Your quotes are ready</h2>
        <p className="lv2-step-description">We've checked the market and found options for your pension.</p>
      </div>

      <div className="lv2-quote-result">
        <div>
          <span>Best lifetime annuity</span>
          <strong>Thanks - a specialist will be in touch</strong>
          <p>Your details have been saved. A retirement specialist will review your options with you shortly.</p>
        </div>
        <div className="lv2-result-pill">Up to 47% more</div>
      </div>
    </div>
  );
}

function QuoteHeroCard({ quote, onBook }: { quote: IQuote; onBook: () => void }) {
  const logo = getQuoteLogo(quote.providerName);
  const uplift = Math.max(1, Math.round(Number(quote.annuityAmountPA ?? 0) * 0.064));
  return (
    <article className="lv2-quote-card">
      <div className="lv2-quote-card-main">
        {logo && <img src={logo} alt={quote.providerName} />}
        <div>
          <strong>{quote.providerName}</strong>
          <span>{getQuoteDetail(quote)}</span>
        </div>
      </div>
      <p className="lv2-quote-card-amount">
        {formatCurrency(quote.annuityAmountPA)}
      </p>
      <div className="lv2-quote-uplift">
        <span aria-hidden="true" className="lv2-quote-uplift-faces">
          <img src={variantBAssets.advisorSmall1} alt="" />
          <img src={variantBAssets.advisorSmall2} alt="" />
          <img src={variantBAssets.advisorSmall3} alt="" />
        </span>
        Our pension specialists could get you an extra <strong>{formatCurrency(uplift)}/year</strong> on this annuity
      </div>
      <button type="button" className="lv2-next-btn ux2-text-button" onClick={onBook}>
        BOOK A FREE APPOINTMENT
      </button>
    </article>
  );
}

function QuoteRow({ quote, onBook }: { quote: IQuote; onBook: () => void }) {
  const logo = getQuoteLogo(quote.providerName);
  return (
    <div className="lv2-quote-row">
      {logo && <img src={logo} alt={quote.providerName} />}
      <strong>{formatCurrency(quote.annuityAmountPA)}</strong>
      <span>{getQuoteDetail(quote)}</span>
      <button type="button" className="ux2-text-button" onClick={onBook}>
        Enquire about this quote <i className="bi bi-chevron-right" aria-hidden="true"></i>
      </button>
    </div>
  );
}

function QuoteResultsGroup({
  group,
  onOpenBooking,
  onOpenInfo,
}: {
  group: QuoteGroup;
  onOpenBooking: () => void;
  onOpenInfo: (type: QuoteExplainerType) => void;
}) {
  const [showRows, setShowRows] = useState(true);

  return (
    <section className="lv2-quotes-group" aria-label={group.title}>
      <div className="lv2-quotes-section-heading">
        <h2 className="lv2-quotes-section-title ux2-text-heading">{group.title}</h2>
        <button type="button" className="lv2-info-link" onClick={() => onOpenInfo(group.type)}>
          What is this?
        </button>
      </div>
      <div className="lv2-quotes-group-card">
        <QuoteHeroCard quote={group.hero} onBook={onOpenBooking} />

        {group.rows.length > 0 && showRows && (
          <>
            <div className="lv2-quotes-table" aria-label={`Additional ${group.title.toLowerCase()} quotes`}>
              {group.rows.map((quote, index) => (
                <QuoteRow key={quoteKey(quote, index)} quote={quote} onBook={onOpenBooking} />
              ))}
            </div>
          </>
        )}

        {group.rows.length > 0 && (
          <button type="button" className="lv2-quotes-hide" onClick={() => setShowRows((value) => !value)}>
            {showRows ? (
              <>
                Hide <i className="bi bi-chevron-up" aria-hidden="true"></i>
              </>
            ) : (
              <>
                Show all <i className="bi bi-chevron-down" aria-hidden="true"></i>
              </>
            )}
          </button>
        )}
      </div>
    </section>
  );
}

function QuotesResultsPanel({
  quotes,
  onOpenBooking,
  onOpenInfo,
}: {
  quotes: IQuote[];
  onOpenBooking: () => void;
  onOpenInfo: (type: QuoteExplainerType) => void;
}) {
  const groups = getQuoteGroups(quotes);

  return (
    <section className="lv2-quotes-results">
      <h1>Here are your quotes</h1>
      <div className="lv2-quotes-results-grid">
        <div className="lv2-quotes-list">
          {groups.map((group) => (
            <QuoteResultsGroup
              key={group.title}
              group={group}
              onOpenBooking={onOpenBooking}
              onOpenInfo={onOpenInfo}
            />
          ))}
        </div>

        <aside className="lv2-quotes-advice" aria-label="Free expert advice">
          <h2 className="ux2-text-heading">Not sure which is right for you?</h2>
          <div className="lv2-quotes-advisor-faces" aria-hidden="true">
            <img src={variantBAssets.advisorSmall1} alt="" />
            <img src={variantBAssets.advisorSmall2} alt="" />
            <img src={variantBAssets.advisorSmall3} alt="" />
          </div>
          <p className="ux2-text-body-lg">Our expert Pension Advisors will help on the right product, at the very best rates</p>
          <QuoteAdviceActions
            actionsClassName="lv2-quotes-advice-actions"
            buttonClassName="lv2-next-btn ux2-text-button"
            callHref={landerCallHref}
            callNumber={landerCallNumber}
            className="lv2-quotes-callout"
            onBook={onOpenBooking}
          />
          <div className="lv2-quotes-review-card">
            <h3 className="ux2-text-heading">We've helped over <strong>1,000</strong> customers get the most from their pension</h3>
            <TrustpilotReviewsCarousel mode="single" reviewerAvatarPosition="top" />
          </div>
        </aside>
      </div>

      <QuoteBoostBand
        buttonClassName="lv2-next-btn ux2-text-button"
        callHref={landerCallHref}
        callNumber={landerCallNumber}
        className="lv2-quotes-boost-band"
        freeTextStrong={false}
        onBook={onOpenBooking}
        phoneNumberStrong
      />
    </section>
  );
}

// Quotes are fetched before this step opens, so it only ever renders what the journey holds.
export function QuotesStep({ quotes, goToStep }: LanderV2StepProps) {
  const [infoDrawerType, setInfoDrawerType] = useState<QuoteExplainerType | null>(null);

  const handleOpenBooking = () => {
    goToStep("booking");
  };

  if (quotes.length === 0) {
    return <QuotesFallbackPanel />;
  }

  return (
    <>
      <QuotesResultsPanel
        quotes={quotes}
        onOpenBooking={handleOpenBooking}
        onOpenInfo={setInfoDrawerType}
      />
      {infoDrawerType && <QuoteExplainerDrawer type={infoDrawerType} onClose={() => setInfoDrawerType(null)} />}
    </>
  );
}
